import React from "react";
import type { Member } from "../../../../Data/member.dto";
import type { TeamMemberPaymentFilterParams } from "../../../../Redux/ApiCalls/Dashboard/TeamMemberPaymentApi";
import BrandDatePicker from "../../Utlis/BrandDatePicker";
import { FormSelect } from "../../../organisms/Dashboard/formSharedStyles";

const PAYMENT_TYPES = [
  { value: "", label: "All types" },
  { value: "salary", label: "Salary" },
  { value: "bonus", label: "Bonus" },
  { value: "advance", label: "Advance" },
  { value: "reimbursement", label: "Reimbursement" },
  { value: "other", label: "Other" },
];

const PAGE_SIZES = [10, 15, 25, 50];

interface TeamMemberPaymentFilterProps {
  members: Member[];
  filters: TeamMemberPaymentFilterParams;
  onFiltersChange: (filters: TeamMemberPaymentFilterParams) => void;
  onApply: (overrideFilters?: TeamMemberPaymentFilterParams) => void;
  loading?: boolean;
  totalPages: number;
  currentPage: number;
  count: number;
  onPageChange: (page: number) => void;
}

export default function TeamMemberPaymentFilter({
  members,
  filters,
  onFiltersChange,
  onApply,
  loading,
  totalPages,
  currentPage,
  count,
  onPageChange,
}: TeamMemberPaymentFilterProps) {
  const handleChange = (
    e: { target: { name?: string; value: string } }
  ) => {
    const { name, value } = e.target;
    if (!name) return;
    onFiltersChange({
      ...filters,
      [name]: value === "" ? undefined : value,
      ...(name === "page_size" ? { page: 1 } : {}),
    });
  };

  const handleClear = () => {
    const cleared = { page: 1, page_size: 15 };
    onFiltersChange(cleared);
    onApply(cleared);
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-4 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Team Member
          </label>
          <FormSelect
            name="team_member_id"
            value={filters.team_member_id ?? ""}
            onChange={handleChange}
          >
            <option value="">All members</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} {m.job_type ? `(${m.job_type})` : ""}
              </option>
            ))}
          </FormSelect>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Type
          </label>
          <FormSelect
            name="payment_type"
            value={filters.payment_type ?? ""}
            onChange={handleChange}
          >
            {PAYMENT_TYPES.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </FormSelect>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <BrandDatePicker
            name="start_date"
            value={filters.start_date ?? ""}
            size="compact"
            onChange={(v) =>
              onFiltersChange({
                ...filters,
                start_date: v === "" ? undefined : v,
              })
            }
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <BrandDatePicker
            name="end_date"
            value={filters.end_date ?? ""}
            size="compact"
            onChange={(v) =>
              onFiltersChange({
                ...filters,
                end_date: v === "" ? undefined : v,
              })
            }
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onApply()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#FFC951] to-[#C94DAA] hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Loading..." : "Apply"}
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">
            Show
          </span>
          <FormSelect
            name="page_size"
            value={filters.page_size ?? 15}
            onChange={handleChange}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </FormSelect>
          <span className="text-sm text-gray-600">per page</span>
          {(count > 0 || currentPage > 1) && (
            <span className="text-sm text-gray-500">
              {count} result{count !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
              className="px-3 py-1.5 rounded border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              className="px-3 py-1.5 rounded border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
